import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { Dialog, Tabs } from 'radix-ui';
import { type GeneratorType, UpgradeConfigKey } from '../../types/game-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SmallButton } from '../../gui/components/SmallButton';
import { Icon } from '../../gui/Icon';
import { upgradesConfig } from '../../upgrades-config';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { HotkeyHint } from '@game-libs/exc-libs';
import { ResourcesList } from '../ResourcesList';
import { useHotkeys } from 'react-hotkeys-hook';
import { MiniManaMeter } from '../../gui/components/MiniManaMeter';
import { EventsManager } from '@game-libs/events-manager';
import { GameplayEvents } from '../../types/game-events';
import { MenuEvents } from '@game-libs/models';
import { AudioManager } from '@game-libs/audio-manager';
import { TreeView } from './TreeView';
import { ListView } from './ListView';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { UpgradesSelectorList } from './UpgradesSelectorList';
import { useHasPurchasableUpgradesMap } from '../../store/hooks/useHasPurchasableUpgrade';
import { useGeneratorsStore } from '../../store/zustand/useGeneratorsStore';

export const UpgradesTreeMenu = () => {
  const { playSFXNow } = AudioManager();
  const ref = useRef(null);
  const [sizeRef, size] = useElementSize();
  const { on } = EventsManager();
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation('upgrades');
  const [selectedElement, setSelectedElement] = useState<UpgradeConfigKey>('click');
  const [isUpgradesOpen, setIsUpgradesOpen] = useState(false);
  const { state: globalState, actions } = GlobalGameStore();
  const [viewMode, setViewMode] = useState<'tree' | 'list'>(globalState.globalVariables.value.viewMode as 'tree' | 'list' || 'tree');


  const generatorsUpgradesConfig = upgradesConfig;

  const tabListIds = useMemo(
    () => Object.keys(generatorsUpgradesConfig),
    [generatorsUpgradesConfig]
  );

  const hasPurchasableMap = useHasPurchasableUpgradesMap(tabListIds);
  const hasAnyPurchasable = tabListIds.some((id) => hasPurchasableMap[id]);
  const generators = useGeneratorsStore((store) => store.generators);
  const generatorPurchasedMap = useMemo(
    () =>
      tabListIds.reduce<Record<string, boolean>>((acc, id) => {
        // Click upgrades are always available (no generator to purchase)
        acc[id] = id === 'click' || id === 'BUG_HIDE' || (generators[id as keyof typeof generators]?.owned ?? 0) > 0;
        return acc;
      }, {}),
    [tabListIds, generators],
  );

  useHotkeys(
    tabListIds.map((id, index) => index + ''),
    ({ key }) => {
      if (generatorPurchasedMap[tabListIds[+key]]) {
        playSFXNow(MenuEvents.CHANGE_VALUE);
        setSelectedElement(tabListIds[+key] as UpgradeConfigKey);
        setIsUpgradesOpen(() => true);
      }
    },
    [generatorPurchasedMap],
  );

  useHotkeys(
    'e',
    () => {
      setIsUpgradesOpen(() => !isUpgradesOpen);
    },
    [isUpgradesOpen],
  );

  const handleDrag = (isDragging: boolean) => {
    setIsDragging(isDragging);
  };

  useEffect(() => {
    // Closes upgrades if last hit is imminent
    return on(GameplayEvents.WAIT_FOR_LAST_HIT, () => {
      setIsUpgradesOpen(false);
    });
  }, []);

  return (
    <Dialog.Root open={isUpgradesOpen} onOpenChange={setIsUpgradesOpen}>
      <Dialog.Overlay className="DialogOverlay" />
      <Dialog.Trigger asChild>
        <button
          className={styles.upgradesButton + ' upgradesButtonBorder'}
          onClick={() => {
            playSFXNow(MenuEvents.CHANGE_VALUE);
          }}
          type="button"
          data-testid="upgrades-tree-menu-button"
        >
          <div className={styles.selectorButtonWrapper}>
            <Icon large iconId="skill_tree" hasBackground={true} />
            <span>{t('upgrades')}</span>
            <HotkeyHint hint="e" />
            {hasAnyPurchasable && (
              <div className={styles.purchasableIndicator}>
                <Icon large iconId="exclamation" />
              </div>
            )}
          </div>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal container={document.getElementById('ui-wrapper')}>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          onCloseAutoFocus={() => playSFXNow(MenuEvents.CHANGE_VALUE)}
          className={`${styles.upgradesTreeContainer} ${viewMode === 'list' ? styles.listViewMode : ''}`}
        >
          <Dialog.Title className={styles.upgradesHeader}>
            {t('upgrades')}
            <Dialog.Close data-testid="close-upgrades-tree-menu-button">
              <Icon hasBackground={false} large iconId={'close_button'} type={'buttons'} />
            </Dialog.Close>
          </Dialog.Title>
          <Tabs.Root
            id="upgrades-dialog"
            className={styles.upgradesContent}
            onValueChange={(value) => {
              setSelectedElement(value as GeneratorType);
              playSFXNow(MenuEvents.CHANGE_VALUE);
            }}
            defaultValue={tabListIds[0]}
          >
            <Tabs.List className={styles.tabList}>
              <UpgradesSelectorList
                tabListIds={tabListIds}
                selectedElement={selectedElement}
                hasPurchasableMap={hasPurchasableMap}
                generatorPurchasedMap={generatorPurchasedMap}
              />
              <div className={styles.viewToggle}>
                <SmallButton
                  onClick={() => {
                    playSFXNow(MenuEvents.CHANGE_VALUE);
                    setViewMode('tree');
                    actions.setGlobalVariables({ viewMode: 'tree' });
                  }}
                  type={viewMode === 'tree' ? 'red' : 'gray'}
                >
                  <Icon large hasBackground={false} iconId="skill_tree" />
                </SmallButton>
                <SmallButton
                  onClick={() => {
                    playSFXNow(MenuEvents.CHANGE_VALUE);
                    setViewMode('list');
                    actions.setGlobalVariables({ viewMode: 'list' });
                  }}
                  type={viewMode === 'list' ? 'red' : 'gray'}
                >
                  <Icon large hasBackground={false} iconId="skill_list" />
                </SmallButton>
              </div>
            </Tabs.List>
            {viewMode === 'tree' ? (
              <TreeView
                sizeRef={sizeRef}
                size={size}
                nodeRef={ref}
                isDragging={isDragging}
                onDragChange={handleDrag}
                selectedElement={selectedElement}
              />
            ) : (
              <ListView selectedElement={selectedElement} />
            )}
          </Tabs.Root>
          <div className={styles.resourcesContainer}>
            <ResourcesList isLarge={false} />
            {generatorPurchasedMap['wizard'] && <MiniManaMeter />}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
